// Las imágenes de REFERENCIA de una generación: cuántas entran, cuáles sobreviven al
// recorte y QUÉ ES cada una — ADR-2026-09-08-diez-referencias-y-recorte-por-prioridad y
// ADR-2026-09-08-las-referencias-del-agente-viajan-etiquetadas.
//
// El tope de 6 estaba repetido en cinco lugares y no venía de ningún modelo: se eligió al
// migrar el modo de prompt libre desde n8n. Sube a 10, el techo del proveedor MÁS
// restrictivo que usamos (Seedream pro; pasarse ahí hace fallar el request). Gemini y
// OpenAI no tienen tope de cantidad: su límite es el peso del pedido.
//
// Y cada referencia viaja con su TIPO, que sirve para dos cosas:
//   1. ORDENAR. Si se pasan del tope, cae el fondo y nunca la foto del producto.
//   2. ETIQUETAR. El backend arma con él la etiqueta que precede a la imagen en el
//      pedido al motor ("Producto 1 vista frontal:", "Pose de referencia:"), como viene
//      haciendo el modo Modelo desde siempre. Sin etiqueta, el motor recibe cuatro fotos
//      sueltas y no sabe cuál es el producto, cuál la persona y cuál el lugar.
//      En VIDEO la etiqueta no puede ir antes de cada imagen —el proveedor toma una lista
//      de URLs y un solo prompt—, así que el backend la convierte en una línea del prompt
//      con la sintaxis que Seedance entiende: "@Image1 es el producto"
//      (ADR-2026-09-12-las-referencias-del-video-se-nombran-en-el-prompt). El tipo es el
//      mismo; lo que cambia es en qué se traduce.
//
// El TEXTO de las etiquetas NO vive acá: lo arma el backend, que ya tiene la tabla
// canónica y —en el caso del producto— la vista de cada foto, que el front no conoce.
// Acá viaja el tipo, no la prosa.
//
// Todo puro, para poder probarlo.

/** Tope de imágenes de referencia por generación de IMAGEN. */
import { leerFirma } from '../../utils/firmaDeStorage';

export const MAX_REFERENCIAS = 10;

/**
 * Tope del VIDEO: otro proveedor, y su contrato de `multi_reference` no documenta
 * cuántas acepta. Es el MISMO número que valida `creatorBody` en el backend, y por eso
 * vive acá: el front mandaba hasta 10 (el de imagen) contra una ruta que acepta 6, así
 * que con 7 referencias el pedido moría en un 400 antes de encolar nada —sin fila en
 * `executions`, o sea sin rastro de que el usuario lo intentó
 * (ADR-2026-09-12-el-video-recortaba-en-la-ruta-y-no-en-la-pantalla).
 */
export const MAX_REFERENCIAS_VIDEO = 6;

/**
 * Qué es cada referencia. El orden del array ES la prioridad: lo primero sobrevive al
 * recorte. El criterio es cuánto se pierde si esa imagen no viaja:
 *  • `producto`, `logo` — el motor los INVENTA si no los ve. Irreemplazables.
 *  • `modelo` — la identidad de la persona; describirla en palabras no la reproduce.
 *  • `adjunto`, `generada` — lo que el usuario acaba de elegir a mano.
 *  • `pose`, `fondo` — los últimos a propósito: además de la foto, el prompt los
 *    describe en palabras, así que degradan sin romper la imagen.
 *
 * Los nombres son los que entiende el backend (`tipo` de `reference_images`): cambiar
 * uno rompe la etiqueta del otro lado.
 */
export const TIPOS_REFERENCIA = [
  'producto',
  'logo',
  'modelo',
  'adjunto',
  'generada',
  'pose',
  'fondo',
] as const;

export type TipoReferencia = (typeof TIPOS_REFERENCIA)[number];

export interface ReferenciaEtiquetada {
  /**
   * La foto. **Puede faltar en `tipo: 'producto'`**, y ese caso importa: un producto
   * traído de Tiendanube guarda el link del CDN de la tienda hasta la primera vez que se
   * usa (el import es lazy, ADR-2026-08-24-tn-import-lazy), y el backend rechaza toda
   * referencia cuya URL no sea de NUESTRO Storage —es su guarda contra descargar de
   * cualquier lado—. Sin URL, el worker la resuelve por `productId` contra el catálogo y
   * baja la foto él mismo, que es lo que ya hacen las pantallas
   * (ADR-2026-09-21-el-producto-recien-traido-viaja-por-id-y-no-por-link).
   */
  url?: string;
  tipo: TipoReferencia;
  /**
   * Sólo en `tipo: 'producto'`: el id de la variante. Con él, el backend baja las HASTA
   * 4 fotos del producto y las etiqueta con la vista real de cada una ("Producto 1 vista
   * frontal:"), igual que el modo Modelo — la URL queda como respaldo para cuando no
   * puede resolverlas (ADR-2026-09-08-el-producto-del-agente-viaja-con-sus-vistas).
   */
  productId?: string;
  /**
   * Lo que el AGENTE dice que es la imagen cuando el tipo no alcanza (una foto adjunta
   * que es la pose, el producto, la persona, el fondo o el logo, o algo que el prompt
   * necesita nombrar: "Estampa a copiar"). El backend la convierte en el rótulo y, si
   * empieza con un rol, en el bloque de protocolo de ese rol
   * (ADR-2026-09-22-el-agente-etiqueta-lo-que-adjunta-el-usuario).
   */
  etiqueta?: string;
  /**
   * Sólo en `tipo: 'producto'` del VIDEO: qué vista del producto es esta foto
   * ("frontal", "espalda"…). El video manda cada vista como una imagen propia y la
   * leyenda del prompt las nombra como vistas de UN producto
   * (ADR-2026-09-24-el-video-elige-sus-referencias-por-importancia).
   */
  vista?: string;
  /**
   * De qué adjunto de la caja salió esta referencia. Es interno del front y NO viaja al
   * backend (los bodies copian campo por campo): sirve para traducir `@Nombre` ↔ `@ImageN`
   * por identidad, sabiendo en qué posición quedó cada adjunto después de priorizar
   * (ADR-2026-10-04-las-menciones-de-video-se-ven-por-nombre).
   */
  adjuntoId?: string;
  /**
   * Con qué nombre la menciona el usuario en el prompt (`@Remera oversize`). Viaja en el
   * pedido de IMAGEN del modo Redes: el backend reemplaza cada `@mención` por el rótulo que
   * el motor ve delante de esa foto
   * (ADR-2026-10-04-el-escritor-de-imagen-usa-las-reglas-del-agente).
   */
  mencion?: string;
}

/** Lo que el agente escribe de una referencia adjunta: cuál ("Imagen N", como la ve en el
 *  mensaje) y qué es. Sin `imagen`, con una sola adjunta es ésa. */
export interface EtiquetaDeAdjunto {
  imagen?: number;
  etiqueta?: string;
}

/**
 * Aplica a las adjuntas del mensaje las etiquetas que el agente escribió. Las adjuntas
 * viajan en el orden en que el modelo las vio numeradas ("Imagen 1", "Imagen 2"…), así
 * que `imagen: 2` es la segunda de tipo `adjunto`. Sin `imagen`, la etiqueta va a la
 * única adjunta; con varias y sin número, no se adivina. Devuelve copias: no toca lo
 * que el hook tiene guardado para el resto del turno.
 */
export function etiquetarAdjuntas(
  refs: readonly ReferenciaEtiquetada[],
  specs: readonly EtiquetaDeAdjunto[],
): ReferenciaEtiquetada[] {
  const out = refs.map((r) => ({ ...r }));
  const adjuntas = out.filter((r) => r.tipo === 'adjunto');
  for (const s of specs) {
    const etiqueta = (s.etiqueta ?? '').trim();
    if (!etiqueta) continue;
    const i = Number.isInteger(s.imagen) && (s.imagen as number) >= 1 ? (s.imagen as number) - 1 : adjuntas.length === 1 ? 0 : -1;
    const target = adjuntas[i];
    if (target) target.etiqueta = etiqueta;
  }
  return out;
}

/**
 * Con qué `tipo` viaja al motor una referencia del catálogo del usuario. Pose y fondo
 * van con su nombre; una foto de MOODBOARD va como `adjunto` —
 * ADR-2026-09-13-los-moodboards-entran-a-la-biblioteca-del-agente.
 *
 * `moodboard` es una etiqueta del FRONT (el chip dice "Moodboard" y el modelo la ve como
 * [MOODBOARD: …]), no del contrato con el backend: allá `tipo` es un enum cerrado
 * (`z.enum(TIPOS_REFERENCIA)` en `lanzarGenerateAd`) y un valor nuevo rebota la
 * generación entera hasta que el backend se deploye. Como `adjunto` el motor la rotula
 * "Imagen de referencia", que es exactamente lo que es, y no hay orden de deploy. Un
 * ELEMENTO DE MARCA (ADR-2026-10-19-elementos-de-marca) va igual, por lo mismo.
 */
export const tipoDeReferenciaParaElMotor = (
  tipo: 'pose' | 'fondo' | 'moodboard' | 'elemento' | 'tipografia',
): TipoReferencia => (tipo === 'pose' || tipo === 'fondo' ? tipo : 'adjunto');

/**
 * El rótulo con que el motor recibe una referencia del catálogo, cuando su tipo no alcanza.
 * La hoja de muestra de una tipografía sin rótulo sería "Imagen de referencia" y el motor la
 * copiaría como imagen; con él, el prompt la nombra como la letra a usar
 * (ADR-2026-10-19-la-tipografia-viaja-como-hoja-de-muestra).
 */
export const etiquetaDeReferenciaParaElMotor = (r: { tipo: string; name: string }): string | undefined =>
  r.tipo === 'tipografia' ? `Tipografía de la marca «${r.name}»` : undefined;

const rango = (t: TipoReferencia): number => {
  const i = TIPOS_REFERENCIA.indexOf(t);
  // Un tipo desconocido no se cuela adelante: va al fondo.
  return i === -1 ? TIPOS_REFERENCIA.length : i;
};

/**
 * Las referencias listas para mandar: sin vacías, sin repetidas, ordenadas por prioridad
 * y recortadas al tope.
 *
 * El orden es ESTABLE dentro de cada tipo: entre dos productos gana el que el usuario
 * eligió primero. Y una foto repetida con dos tipos queda una sola vez, con el de MAYOR
 * prioridad (la misma foto adjuntada y elegida como producto no ocupa dos lugares ni
 * pierde su rango de producto).
 *
 * "Repetida" se mide por el OBJETO de Storage, no por el string de la URL
 * (ADR-2026-09-14-la-pose-y-el-fondo-son-referencias-por-id): la misma pose enganchada por
 * el usuario y nombrada por el agente con su `pose_id` llega dos veces, firmada en dos
 * momentos, o sea con dos URLs distintas. Comparando strings viajaba dos veces: gastaba dos
 * de los diez lugares y —desde que las repetidas se numeran— el motor recibía
 * "Pose de referencia 1:" y "2:" que son la MISMA foto.
 */
export function priorizarReferencias(
  items: ReferenciaEtiquetada[],
  max: number = MAX_REFERENCIAS,
): ReferenciaEtiquetada[] {
  const mejorPorFoto = new Map<
    string,
    {
      url?: string;
      orden: number;
      rango: number;
      tipo: TipoReferencia;
      productId?: string;
      etiqueta?: string;
      vista?: string;
      adjuntoId?: string;
    }
  >();
  items.forEach((it, orden) => {
    const url = (it.url ?? '').trim();
    // Sin URL sólo sobreviven un producto, que el backend sabe resolver por su id, y el
    // logo, que resuelve contra la marca. El resto sin URL no es nada que se pueda mandar.
    if (!url && !it.productId && it.tipo !== 'logo') return;
    // La misma foto firmada dos veces son dos URLs y UN objeto: se agrupa por el objeto.
    // Un producto sin URL se agrupa por su id: dos productos distintos no se pisan. El
    // logo sin URL es uno solo: el de la marca.
    const firma = url ? leerFirma(url) : null;
    const clave = url
      ? firma
        ? `${firma.bucket}/${firma.path}`
        : url
      : it.productId
        ? `producto:${it.productId}`
        : 'logo';
    const r = rango(it.tipo);
    const previa = mejorPorFoto.get(clave);
    const entrada = {
      // Se conserva la URL de la primera aparición: es la que ya viajó por el resto del
      // turno (los bloques de imagen del mensaje), así que no se cambia por otra firma.
      url: previa?.url ?? (url || undefined),
      orden: previa?.orden ?? orden,
      rango: r,
      tipo: it.tipo,
      ...(it.productId ? { productId: it.productId } : {}),
      ...(it.vista ? { vista: it.vista } : {}),
      // El adjunto de origen es el de la primera aparición, como la URL.
      ...(previa?.adjuntoId || it.adjuntoId ? { adjuntoId: previa?.adjuntoId ?? it.adjuntoId } : {}),
      // La etiqueta sobrevive a la deduplicación: la foto repetida se queda con la que
      // alguien le puso, venga de la aparición que venga.
      ...(it.etiqueta || previa?.etiqueta ? { etiqueta: it.etiqueta || previa?.etiqueta } : {}),
    };
    // Se conserva la primera aparición como orden (estabilidad) y el mejor rango.
    if (!previa || r < previa.rango) mejorPorFoto.set(clave, entrada);
    else if (entrada.etiqueta && !previa.etiqueta) previa.etiqueta = entrada.etiqueta;
  });
  return [...mejorPorFoto.values()]
    .sort((a, b) => a.rango - b.rango || a.orden - b.orden)
    .slice(0, Math.max(0, max))
    .map((v) => ({
      ...(v.url ? { url: v.url } : {}),
      tipo: v.tipo,
      ...(v.productId ? { productId: v.productId } : {}),
      ...(v.etiqueta ? { etiqueta: v.etiqueta } : {}),
      ...(v.vista ? { vista: v.vista } : {}),
      ...(v.adjuntoId ? { adjuntoId: v.adjuntoId } : {}),
    }));
}

/**
 * Los que degradan sin romper: la pose y el fondo, que además de la foto el prompt los
 * describe en palabras. Son lo último que entra, detrás de las vistas extra.
 */
const SECUNDARIAS: ReadonlySet<TipoReferencia> = new Set(['pose', 'fondo']);

/**
 * Las referencias de un VIDEO, elegidas por importancia —
 * ADR-2026-09-24-el-video-elige-sus-referencias-por-importancia.
 *
 * El video manda una lista plana de fotos (`multi_reference`), así que cada vista de un
 * producto ocupa un lugar propio del tope. Entran por turnos, y dentro de cada turno en el
 * orden de `priorizarReferencias`:
 *   1. La foto principal de CADA producto: sin ella el motor lo inventa.
 *   2. Logo, persona, lo adjuntado y lo ya generado: tampoco se reemplazan con palabras.
 *   3. Las vistas extra de los productos, repartidas de a una por producto (la segunda
 *      de cada uno, después la tercera…): con un solo producto y lugar de sobra, van
 *      sus cuatro fotos.
 *   4. La pose y el fondo.
 * Lo que no entra en el tope se cae del final de esa cola.
 *
 * La salida va en el orden de envío: primero la principal de cada producto y el resto por
 * prioridad —las mismas posiciones que antes de las vistas, que son las que el agente
 * nombra como @Image1, @Image2… en su prompt—, y las vistas extra AL FINAL. Pegadas a su
 * principal corrían a todo lo demás: "@Image2 is the person" pasaba a ser la segunda foto
 * del producto (ADR-2026-09-24-las-referencias-del-video-llevan-la-etiqueta-del-agente).
 * La leyenda del backend las agrupa igual, por `productId`, estén donde estén. Un producto
 * sin id es un grupo de una foto.
 */
export function elegirReferenciasDeVideo(
  items: ReferenciaEtiquetada[],
  max: number = MAX_REFERENCIAS_VIDEO,
): ReferenciaEtiquetada[] {
  // Sin repetidas y en orden de prioridad, sin recortar todavía: el recorte es el de acá.
  // Sin URL sólo viaja el logo: la ruta de video lo resuelve contra la marca antes de
  // validar (ADR-2026-10-05-el-logo-externo-se-baja-y-se-guarda-al-generar). Un producto
  // sin URL no: el video no manda `product_id` para resolverlo.
  const ordenadas = priorizarReferencias(items, Number.POSITIVE_INFINITY).filter(
    (r) => !!r.url || r.tipo === 'logo',
  );
  const grupos = new Map<string, ReferenciaEtiquetada[]>();
  const importantes: ReferenciaEtiquetada[] = [];
  const secundarias: ReferenciaEtiquetada[] = [];
  ordenadas.forEach((r, i) => {
    if (r.tipo === 'producto') {
      const clave = r.productId ? `id:${r.productId}` : `foto:${i}`;
      const grupo = grupos.get(clave);
      if (grupo) grupo.push(r);
      else grupos.set(clave, [r]);
    } else if (SECUNDARIAS.has(r.tipo)) {
      secundarias.push(r);
    } else {
      importantes.push(r);
    }
  });
  const productos = [...grupos.values()];
  const extras: ReferenciaEtiquetada[] = [];
  const masVistas = Math.max(0, ...productos.map((g) => g.length));
  for (let v = 1; v < masVistas; v++) {
    for (const g of productos) if (g[v]) extras.push(g[v]);
  }
  const cola = [...productos.map((g) => g[0]), ...importantes, ...extras, ...secundarias];
  const entran = new Set(cola.slice(0, Math.max(0, max)));
  return [
    ...productos.map((g) => g[0]).filter((r) => entran.has(r)),
    ...importantes.filter((r) => entran.has(r)),
    ...secundarias.filter((r) => entran.has(r)),
    ...extras.filter((r) => entran.has(r)),
  ];
}

/** Sólo las URLs, en orden. Es la forma en la que las referencias terminan llegando al
 *  proveedor de VIDEO (la leyenda del prompt las numera por esta posición), y el atajo
 *  de los tests para mirar el orden sin los tipos. */
export const urlsDeReferencias = (refs: ReferenciaEtiquetada[]): string[] =>
  refs.flatMap((r) => (r.url ? [r.url] : []));

/**
 * ¿Esa URL es un objeto de NUESTRO Storage?
 *
 * Es la misma pregunta que se hace el backend antes de aceptar una referencia
 * (`isOurStorageUrl` en `lanzarGenerateAd`), y la única que importa acá: un link de otro
 * lado —el CDN de Tiendanube, por ejemplo— rebota la generación ENTERA con un 400.
 */
export const esDeNuestroStorage = (url: string): boolean =>
  url.includes('/storage/v1/object/');

/**
 * La referencia de un producto, lista para viajar.
 *
 * Con la foto ya en nuestro Storage viaja con su URL, como siempre. Si lo que hay es un
 * link de afuera —un producto recién traído de Tiendanube, cuya foto todavía vive en el
 * CDN de la tienda— viaja **sólo con el id**: el backend la resuelve contra el catálogo,
 * baja la foto y la deja guardada, así la próxima vez ya es nuestra.
 */
export const referenciaDeProducto = (url: string, productId: string): ReferenciaEtiquetada =>
  esDeNuestroStorage(url)
    ? { url, tipo: 'producto', productId }
    : { tipo: 'producto', productId };

/**
 * La referencia del logo de la marca, lista para viajar. Misma regla que el producto: el
 * análisis del sitio deja el logo como link de afuera (el favicon, el logo del tema de la
 * tienda), y ese link rebotaba la generación ENTERA. Sin URL, el backend lo busca en la
 * marca de la generación, lo baja y lo deja en nuestro Storage, así la próxima vez ya es
 * nuestro (ADR-2026-10-05-el-logo-externo-se-baja-y-se-guarda-al-generar).
 */
export const referenciaDeLogo = (url: string): ReferenciaEtiquetada =>
  esDeNuestroStorage(url) ? { url, tipo: 'logo' } : { tipo: 'logo' };

/** Cuántas referencias sobran respecto del tope (0 = entran todas). Lo usa la UI para
 *  avisar antes de generar, en vez de recortar en silencio. */
export const referenciasQueSobran = (cantidad: number, max: number = MAX_REFERENCIAS): number =>
  Math.max(0, cantidad - max);

/**
 * Una referencia que el usuario ELIGIÓ y no se pudo resolver, y sin la cual la imagen
 * saldría mal y cobrada igual: hoy, la foto del modelo guardado cuando Storage no la firma
 * y la firma vieja venció. Las demás referencias que fallan se saltean con aviso; ésta
 * frena la generación con un mensaje claro
 * (ADR-2026-10-06-sin-la-foto-del-modelo-no-se-genera).
 */
export class ReferenciaIrrecuperable extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ReferenciaIrrecuperable';
  }
}

export const MENSAJE_MODELO_SIN_FOTO = 'No pude abrir la foto del modelo elegido. Probá de nuevo.';
export const MENSAJE_ADJUNTA_SIN_FIRMA = 'No pude abrir una de las fotos adjuntas. Probá de nuevo.';
